import React, { useState, useEffect, useCallback } from 'react';
import { useSearchFilters } from '../contexts/SearchContext';
import { useIntl } from 'react-intl';

type LastSeenOption = 'all' | '6h' | '24h' | '3d' | '1w' | '2w' | '1m';

interface LastSeenOptionConfig {
  value: LastSeenOption;
  labelKey: string;
  defaultLabel: string;
  hours: number;
}

const LAST_SEEN_OPTIONS: LastSeenOptionConfig[] = [
  { value: 'all', labelKey: 'search.refinements.lastSeen.all', defaultLabel: 'All', hours: 0 },
  { value: '6h', labelKey: 'search.refinements.lastSeen.past6Hours', defaultLabel: 'Past 6 hours', hours: 6 },
  { value: '24h', labelKey: 'search.refinements.lastSeen.past24Hours', defaultLabel: 'Past 24 hours', hours: 24 },
  { value: '3d', labelKey: 'search.refinements.lastSeen.past3Days', defaultLabel: 'Past 3 days', hours: 72 },
  { value: '1w', labelKey: 'search.refinements.lastSeen.past1Week', defaultLabel: 'Past 1 week', hours: 168 },
  { value: '2w', labelKey: 'search.refinements.lastSeen.past2Weeks', defaultLabel: 'Past 2 weeks', hours: 336 },
  { value: '1m', labelKey: 'search.refinements.lastSeen.past1Month', defaultLabel: 'Past 1 month', hours: 720 },
];

interface LastSeenSelectorProps {
  className?: string;
  isMobile?: boolean;
}

export const LastSeenSelector: React.FC<LastSeenSelectorProps> = ({ className = '', isMobile = false }) => {
  const intl = useIntl();
  const { filters, setFilters } = useSearchFilters();
  const filterObj = (filters && typeof filters === 'object') ? (filters as Record<string, string[]>) : {};

  // Convert timestamp back to option value
  const getOptionFromTimestamp = useCallback((timestamp: string | undefined): LastSeenOption => {
    if (!timestamp || timestamp === 'all') return 'all';
    const num = Number(timestamp);
    if (isNaN(num)) return 'all';
    
    const now = Date.now();
    
    // Find the option that would produce a threshold closest to the stored timestamp
    // This finds which option was originally selected, even if time has passed
    let bestMatch: LastSeenOption = 'all';
    let smallestDiff = Infinity;
    
    // Check each option to see which one would produce a threshold closest to the stored timestamp
    for (const option of LAST_SEEN_OPTIONS) {
      if (option.hours > 0) {
        // Calculate what the threshold would be if this option were selected now
        const expectedThreshold = now - (option.hours * 60 * 60 * 1000);
        const diff = Math.abs(num - expectedThreshold);
        
        // Find the option with the smallest difference
        // The difference represents how much time has passed since the filter was set
        // We want to find the option that was originally selected, so we look for the smallest difference
        if (diff < smallestDiff) {
          smallestDiff = diff;
          bestMatch = option.value;
        }
      }
    }
    
    return bestMatch;
  }, []);

  const [selectedOption, setSelectedOption] = useState<LastSeenOption>(() => {
    const current = filterObj.lastSeenMin?.[0];
    if (current === 'all' || !current) return 'all';
    return getOptionFromTimestamp(current);
  });

  useEffect(() => {
    const current = filterObj.lastSeenMin?.[0];
    if (current === 'all' || !current) {
      setSelectedOption('all');
    } else {
      setSelectedOption(getOptionFromTimestamp(current));
    }
  }, [filterObj.lastSeenMin, getOptionFromTimestamp]);

  const handleChange = useCallback((option: LastSeenOption) => {
    setSelectedOption(option);
    
    const base = filterObj;
    const nextFilters: Record<string, string[]> = { ...base };

    if (option === 'all') {
      delete nextFilters.lastSeenMin;
    } else {
      const optionConfig = LAST_SEEN_OPTIONS.find(o => o.value === option);
      if (optionConfig && optionConfig.hours > 0) {
        const now = Date.now();
        const threshold = now - (optionConfig.hours * 60 * 60 * 1000);
        nextFilters.lastSeenMin = [String(threshold)];
      }
    }

    const hasAny = Object.keys(nextFilters).length > 0;
    setFilters(hasAny ? nextFilters : {});
  }, [filterObj, setFilters]);

  // iOS 16/17/18/26 style select for mobile (matching sort selector)
  const iosSelectClass = 'block min-w-[100px] sm:min-w-[120px] md:min-w-[140px] max-w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition-all duration-200 truncate cursor-pointer';

  if (isMobile) {
    // Mobile: iOS-style select
    return (
      <select
        className={`${iosSelectClass} ${className}`}
        value={selectedOption}
        onChange={(e) => handleChange(e.target.value as LastSeenOption)}
        aria-label={intl.formatMessage({ id: 'search.refinements.lastSeen.aria', defaultMessage: 'Last Seen', description: 'Accessibility label for last seen filter selector' })}
      >
        {LAST_SEEN_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {intl.formatMessage({ id: option.labelKey, defaultMessage: option.defaultLabel })}
          </option>
        ))}
      </select>
    );
  }

  // Desktop: iOS-style select (more compact than segmented control for 6 options)
  return (
    <select
      className={`${iosSelectClass} ${className}`}
      value={selectedOption}
      onChange={(e) => handleChange(e.target.value as LastSeenOption)}
      aria-label={intl.formatMessage({ id: 'search.refinements.lastSeen.aria', defaultMessage: 'Last Seen', description: 'Accessibility label for last seen filter selector' })}
    >
      {LAST_SEEN_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {intl.formatMessage({ id: option.labelKey, defaultMessage: option.defaultLabel })}
        </option>
      ))}
    </select>
  );
};

